import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * HostTwoPillars Component
 * Two feature cards for the Hosts page: "Host on Idle Time" and "Share Your Games"
 * Desktop: Side-by-side cards centered in the 1024px canvas
 * Mobile: Vertically stacked cards
 */

const WHITE = 'rgba(255, 255, 255, 1)';

const LUMINA_BACKGROUND: React.CSSProperties = {
  background:
    'linear-gradient(180deg, #343a8f, #55307f) padding-box, ' +
    'linear-gradient(135deg, #7d9df0, #9a74e3, #b68af0, #6f8fe8) border-box',
  border: '1px solid transparent',
};

const OBSIDIAN_BACKGROUND: React.CSSProperties = {
  background: 'linear-gradient(180deg, #151515, #2b2b2b)',
};

const BUTTON_WHITE_FILL: React.CSSProperties = {
  background: '#ffffff',
  color: '#161423',
};

const BUTTON_DARK_OUTLINE: React.CSSProperties = {
  background: 'transparent',
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.5)',
};

interface PillarCardProps {
  backgroundStyle: React.CSSProperties;
  title: string;
  introText: string;
  bullets: string[];
  buttonLabel: string;
  buttonStyle: React.CSSProperties;
  buttonDisabled?: boolean;
  delay: number;
  isInView: boolean;
  onButtonClick?: () => void;
  flowLayout?: boolean;
}

const PillarCard = ({
  backgroundStyle,
  title,
  introText,
  bullets,
  buttonLabel,
  buttonStyle,
  buttonDisabled,
  delay,
  isInView,
  onButtonClick,
  flowLayout = false,
}: PillarCardProps) => {
  if (flowLayout) {
    return (
      <motion.div
        style={{ position: 'relative', width: '350px', borderRadius: '20px', overflow: 'hidden', padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', ...backgroundStyle }}
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.4, delay, ease: 'easeOut' }}
      >
        <h3 style={{ lineHeight: '30px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '26px', color: WHITE, margin: 0, marginBottom: '12px', fontVariationSettings: "'wght' 900", textAlign: 'center' }}>
          {title}
        </h3>
        <p style={{ lineHeight: '22px', fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '16px', color: WHITE, margin: 0, marginBottom: '30px', textAlign: 'center' }}>
          {introText}
        </p>
        <button
          onClick={buttonDisabled ? undefined : onButtonClick}
          disabled={buttonDisabled}
          style={{ width: '100%', height: '35px', borderRadius: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '18px', border: 'none', cursor: buttonDisabled ? 'default' : 'pointer', opacity: buttonDisabled ? 0.5 : 1, pointerEvents: buttonDisabled ? 'none' : 'auto', transition: 'transform 0.2s ease, opacity 0.2s ease', marginBottom: '30px', ...buttonStyle }}
        >
          <span style={{ height: '35px', lineHeight: '35px' }}>{buttonLabel}</span>
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {bullets.map((bullet, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px', alignItems: 'start' }}>
              <span style={{ lineHeight: '22px', fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '16px', color: WHITE }}>✔</span>
              <span style={{ lineHeight: '22px', fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '16px', color: WHITE }}>{bullet}</span>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      style={{ position: 'relative', width: '320px', height: '258px' }}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    >
      {/* Card background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '20px',
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...backgroundStyle,
        }}
      />

      {/* Title */}
      <div style={{ position: 'absolute', left: '33px', top: '36px', width: '274px', zIndex: 2 }}>
        <h3
          style={{
            lineHeight: '25px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '22px',
            color: WHITE,
            margin: 0,
            fontVariationSettings: "'wght' 900",
          }}
        >
          {title}
        </h3>
      </div>

      {/* Intro text */}
      <div style={{ position: 'absolute', left: '33px', top: '72px', width: '274px', zIndex: 2 }}>
        <p
          style={{
            lineHeight: '14px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '10px',
            color: WHITE,
            margin: 0,
          }}
        >
          {introText}
        </p>
      </div>

      {/* CTA Button */}
      <button
        onClick={buttonDisabled ? undefined : onButtonClick}
        disabled={buttonDisabled}
        style={{
          position: 'absolute',
          left: '29px',
          top: '102px',
          width: '252px',
          height: '24px',
          borderRadius: '30px',
          zIndex: 4,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: '12px',
          border: 'none',
          cursor: buttonDisabled ? 'default' : 'pointer',
          opacity: buttonDisabled ? 0.5 : 1,
          pointerEvents: buttonDisabled ? 'none' : 'auto',
          transition: 'transform 0.2s ease, opacity 0.2s ease',
          ...buttonStyle,
        }}
      >
        <span style={{ height: '24px', lineHeight: '24px' }}>
          {buttonLabel}
        </span>
      </button>

      {/* Bullet points */}
      <div style={{ position: 'absolute', left: '33px', top: '148px', width: '274px', zIndex: 2 }}>
        {bullets.map((bullet, i) => (
          <p
            key={i}
            style={{
              lineHeight: '14px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '10px',
              color: WHITE,
              margin: 0,
              marginBottom: i < bullets.length - 1 ? '10px' : 0,
            }}
          >
            ✔ {bullet}
          </p>
        ))}
      </div>
    </motion.div>
  );
};

const CARD_1_BULLETS = [
  'Earn tokens as you support the network',
  'Choose from a selection of game rewards',
  'Help power the future of instant-play',
];

const CARD_2_BULLETS = [
  'Use P2P game-streaming to share any game',
  "Make your game playable the moment it's discovered",
  'Let players jump in instantly, no install required.',
];

const VIDEO_EMBED_URL = 'https://www.youtube.com/embed/pZrpQGokrpo?autoplay=1';

export const HostTwoPillars = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const openVideo = () => {
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Learn More', buttonLocation: 'HostTwoPillars' });
    setIsVideoOpen(true);
  };

  return (
    <>
      {/* Video Modal — rendered via portal to escape auto-scaler transform context */}
      {createPortal(
        <AnimatePresence>
          {isVideoOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsVideoOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
              }}
            >
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'relative',
                  width: 'min(880px, 90vw)',
                  aspectRatio: '16 / 9',
                }}
              >
                <iframe
                  src={VIDEO_EMBED_URL}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: '12px' }}
                />
                <button
                  onClick={() => setIsVideoOpen(false)}
                  style={{
                    position: 'absolute',
                    top: '-36px',
                    right: 0,
                    background: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '24px',
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    opacity: 0.8,
                  }}
                >
                  ✕
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '40px 0',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Device decorative image */}
        <img
          src="/img/aida/HomeThreePlatformOptions/Option1/threeplatformphone1.png"
          alt=""
          className="fixMobileImageStretch"
          style={{
            position: 'absolute',
            left: '772px',
            top: '63px',
            width: 'auto',
            height: '116px',
            zIndex: 5,
            transform: 'scaleX(-1)',
            filter: 'drop-shadow(rgba(3, 10, 42, 0.23) 8px 6px 2px)',
            pointerEvents: 'none',
          }}
        />

        {/* Gibber character */}
        <img
          src="/img/aida/HostTwoPillars/host-gibber.svg"
          alt=""
          className="fixMobileImageStretch"
          style={{
            position: 'absolute',
            left: '144px',
            top: '180px',
            width: '57px',
            height: '126px',
            zIndex: 5,
            transform: 'rotate(4deg) scaleX(-1)',
            filter: 'drop-shadow(rgba(38, 0, 73, 0.41) -3px 7px 6px)',
            pointerEvents: 'none',
          }}
        />

        {/* Decorative background overlays */}
        <img
          src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp"
          alt=""
          style={{
            position: 'absolute',
            left: '718px',
            top: '-132px',
            width: '590px',
            height: '338px',
            transform: 'rotate(-82deg)',
            opacity: 0.27,
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <img
          src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp"
          alt=""
          style={{
            position: 'absolute',
            left: '-304px',
            top: '-97px',
            width: '590px',
            height: '338px',
            transform: 'rotate(69deg)',
            opacity: 0.27,
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        <motion.div
          className="flex w-full justify-center"
          style={{ gap: '24px' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <PillarCard
            isInView={isInView}
            delay={0}
            backgroundStyle={LUMINA_BACKGROUND}
            title="HOST ON IDLE TIME"
            introText="Earn rewards when you're away from your computer."
            bullets={CARD_1_BULLETS}
            buttonLabel="Learn More"
            buttonStyle={BUTTON_WHITE_FILL}
            onButtonClick={openVideo}
          />
          <PillarCard
            isInView={isInView}
            delay={0.1}
            backgroundStyle={OBSIDIAN_BACKGROUND}
            title="SHARE YOUR GAMES"
            introText="Share your games with your friends."
            bullets={CARD_2_BULLETS}
            buttonLabel="Coming Soon"
            buttonStyle={BUTTON_DARK_OUTLINE}
            buttonDisabled={true}
            onButtonClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Coming Soon', buttonLocation: 'HostTwoPillars' })}
          />
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '40px 0',
          gap: '24px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <PillarCard
          isInView={isMobileInView}
          delay={0}
          backgroundStyle={LUMINA_BACKGROUND}
          title="HOST ON IDLE TIME"
          introText="Earn rewards when you're away from your computer."
          bullets={CARD_1_BULLETS}
          buttonLabel="Learn More"
          buttonStyle={BUTTON_WHITE_FILL}
          onButtonClick={openVideo}
          flowLayout
        />
        <PillarCard
          isInView={isMobileInView}
          delay={0.1}
          backgroundStyle={OBSIDIAN_BACKGROUND}
          title="SHARE YOUR GAMES"
          introText="Share your games with your friends."
          bullets={CARD_2_BULLETS}
          buttonLabel="Coming Soon"
          buttonStyle={BUTTON_DARK_OUTLINE}
          buttonDisabled={true}
          onButtonClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Coming Soon', buttonLocation: 'HostTwoPillars' })}
          flowLayout
        />
      </motion.section>
    </>
  );
};
